import {
    type ImgHTMLAttributes,
    type ReactNode,
    type AnchorHTMLAttributes,
    type HTMLAttributes,
    type ComponentType,
    memo,
    useMemo,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import rehypeExternalLinks from 'rehype-external-links';
import 'katex/dist/katex.min.css';
import './math-overrides.css';
import {
    MarkdownUL,
    MarkdownOL,
    MarkdownStrong,
    MarkdownEm,
    MarkdownLI,
    MarkdownCode,
    MarkdownPre,
    MarkdownBlockquote,
    MarkdownTable,
    MarkdownTh,
    MarkdownTd,
    MarkdownHr,
    MarkdownLink,
    MarkdownH1,
    MarkdownH2,
    MarkdownH3,
    MarkdownH4,
    MarkdownH5,
    MarkdownH6,
    MarkdownParagraph,
    MarkdownImage,
} from './components';

interface RenderMarkdownProps {
    children: ReactNode;
    className?: string;
    // Allow customization of specific components while providing sensible defaults
    components?: {
        a?: ComponentType<AnchorHTMLAttributes<HTMLAnchorElement>>;
        ul?: ComponentType<HTMLAttributes<HTMLElement>>;
        ol?: ComponentType<HTMLAttributes<HTMLElement>>;
        li?: ComponentType<HTMLAttributes<HTMLElement>>;
        strong?: ComponentType<HTMLAttributes<HTMLElement>>;
        b?: ComponentType<HTMLAttributes<HTMLElement>>;
        em?: ComponentType<HTMLAttributes<HTMLElement>>;
        i?: ComponentType<HTMLAttributes<HTMLElement>>;
        p?: ComponentType<HTMLAttributes<HTMLElement>>;
        h1?: ComponentType<HTMLAttributes<HTMLElement>>;
        h2?: ComponentType<HTMLAttributes<HTMLElement>>;
        h3?: ComponentType<HTMLAttributes<HTMLElement>>;
        h4?: ComponentType<HTMLAttributes<HTMLElement>>;
        h5?: ComponentType<HTMLAttributes<HTMLElement>>;
        h6?: ComponentType<HTMLAttributes<HTMLElement>>;
        code?: ComponentType<HTMLAttributes<HTMLElement>>;
        pre?: ComponentType<HTMLAttributes<HTMLElement>>;
        blockquote?: ComponentType<HTMLAttributes<HTMLElement>>;
        table?: ComponentType<HTMLAttributes<HTMLElement>>;
        th?: ComponentType<HTMLAttributes<HTMLElement>>;
        td?: ComponentType<HTMLAttributes<HTMLElement>>;
        hr?: ComponentType;
        img?: ComponentType<ImgHTMLAttributes<HTMLImageElement>>;
    };
}

// oxlint-disable-next-line complexity
function RenderMarkdownComponent({ children, className, components = {} }: RenderMarkdownProps): ReactNode {
    const markdownComponents = useMemo(
        // oxlint-disable-next-line complexity
        () => ({
            // Use shared components as defaults, allow overrides
            a: components.a || MarkdownLink,
            ul: components.ul || MarkdownUL,
            ol: components.ol || MarkdownOL,
            li: components.li || MarkdownLI,
            strong: components.strong || MarkdownStrong,
            b: components.b || MarkdownStrong,
            em: components.em || MarkdownEm,
            i: components.i || MarkdownEm,
            p: components.p || MarkdownParagraph,
            h1: components.h1 || MarkdownH1,
            h2: components.h2 || MarkdownH2,
            h3: components.h3 || MarkdownH3,
            h4: components.h4 || MarkdownH4,
            h5: components.h5 || MarkdownH5,
            h6: components.h6 || MarkdownH6,
            code: components.code || MarkdownCode,
            pre: components.pre || MarkdownPre,
            blockquote: components.blockquote || MarkdownBlockquote,
            table: components.table || MarkdownTable,
            th: components.th || MarkdownTh,
            td: components.td || MarkdownTd,
            hr: components.hr || MarkdownHr,
            img: components.img || MarkdownImage,
        }),
        [components],
    );

    // Convert children to string for ReactMarkdown
    const markdownContent = typeof children === 'string' ? children : String(children);

    return (
        <div className={className}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
                rehypePlugins={[rehypeKatex, [rehypeExternalLinks, { target: '_blank' }]]}
                components={markdownComponents}
            >
                {markdownContent}
            </ReactMarkdown>
        </div>
    );
}

export const RenderMarkdown = memo(RenderMarkdownComponent) as typeof RenderMarkdownComponent;
